import {
    endOfDay,
    endOfMonth,
    endOfWeek,
    format,
    getWeek,
    getYear,
    setWeek,
    startOfDay,
    startOfMonth,
    startOfWeek,
    subDays,
} from "date-fns";
import { formatWeek } from "side-front-utils";

import { getLocale } from "@lib/utils/getLocale";

import type { AttendanceDateModeUnion } from "../searchEnums";

export function getDateRange(args: {
    dateMode: "all";
    day?: Date;
    week?: string;
    month?: string;
}): { startDate: undefined; endDate: undefined };
export function getDateRange(args: {
    dateMode: Exclude<AttendanceDateModeUnion, "all">;
    rollingDays?: "7" | "15" | "30";
    day?: Date;
    week?: string;
    month?: string;
}): { startDate: Date; endDate: Date };
export function getDateRange({
    dateMode,
    rollingDays,
    day,
    week,
    month,
}: {
    dateMode: AttendanceDateModeUnion;
    rollingDays?: "7" | "15" | "30";
    day?: Date;
    week?: string;
    month?: string;
}) {
    const now = new Date();

    switch (dateMode) {
        case "all":
            return { startDate: undefined, endDate: undefined };
        case "rolling": {
            const safeRollingDays = rollingDays === "15" ? 15 : rollingDays === "30" ? 30 : 7;
            const start = startOfDay(subDays(now, safeRollingDays - 1));
            const end = endOfDay(now);
            return { startDate: start, endDate: end };
        }
        case "day": {
            const safeDay = day ?? now;
            return { startDate: startOfDay(safeDay), endDate: endOfDay(safeDay) };
        }
        case "week": {
            const safeWeek = week ?? getWeekObject(now).objectID;
            const [weekNumber, weekYear] = safeWeek.split("-").map(Number);

            const safeWeekDate =
                isNaN(weekNumber) || isNaN(weekYear)
                    ? now
                    : setWeek(new Date(weekYear, 0, 1), weekNumber);

            return { startDate: startOfWeek(safeWeekDate), endDate: endOfWeek(safeWeekDate) };
        }
        case "month": {
            const safeMonth = month ?? `${format(now, "MM")}-${getYear(now)}`;
            const [monthNumber, monthYear] = safeMonth.split("-").map(Number);

            const safeMonthDate =
                isNaN(monthNumber) || isNaN(monthYear)
                    ? now
                    : new Date(monthYear, monthNumber - 1, 1);

            return { startDate: startOfMonth(safeMonthDate), endDate: endOfMonth(safeMonthDate) };
        }
    }
}

export function getWeekObject(date: Date) {
    const locale = getLocale();
    const startDate = startOfWeek(date);
    const endDate = endOfWeek(date);

    const weekNumber = getWeek(date);

    const weekYear =
        getYear(startDate) === getYear(endDate)
            ? getYear(startDate)
            : weekNumber === 1
              ? getYear(endDate)
              : getYear(startDate);

    return {
        objectID: `${weekNumber}-${weekYear}`,
        value: `${weekNumber}-${weekYear}`,
        label: `${locale.code === "fr" ? "Semaine" : "Week"} ${weekNumber} ${weekYear}`,
        subLabel: formatWeek(startDate),
        startDate,
        endDate,
    };
}
